/**
 * Bottom Navigation Bar
 * 
 * Sticky bottom navigation that appears after scrolling.
 */

import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Config from '../../Config';
import { useLanguage } from '../../i18n';

const BottomNav = () => {
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const { t } = useLanguage();

  // Show bottom nav after scrolling down 200px
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setIsVisible(scrollY > 200);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { path: '/', label: t('nav.home'), icon: '🏠' },
    { path: '/blog', label: t('nav.blog'), icon: '✍️' },
    { path: '/resume', label: t('nav.resume'), icon: '📄' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Desktop: Author Info Bar - slides up when visible */}
      <div 
        className={`hidden md:block fixed left-0 right-0 bg-white border-t border-gray-200 z-40 transition-all duration-300 ${
          isVisible ? 'bottom-0 translate-y-0' : 'bottom-0 translate-y-full'
        }`}
      >
        <div className="max-w-6xl mx-auto px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Avatar */}
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-900 to-gray-700 flex items-center justify-center text-white font-bold text-sm">
              {Config.initials}
            </div>
            <div>
              <div className="font-medium text-gray-900">{Config.name}</div>
              <div className="text-xs text-gray-500">{t('jobTitle')}</div>
            </div>
          </div>

          {/* Quick Nav */}
          <div className="flex items-center gap-6">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`text-sm font-medium transition-colors ${
                  isActive(item.path) ? 'text-blue-600' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          {/* Back to Top */}
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors px-3 py-2 rounded-lg hover:bg-gray-100"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
            </svg>
            {t('blog.backToTop') || 'Top'}
          </button>
        </div>
      </div>

      {/* Mobile: Bottom Tab Bar - slides up when visible */}
      <div 
        className={`md:hidden fixed left-0 right-0 bg-white border-t border-gray-200 z-50 pb-safe transition-all duration-300 ${
          isVisible ? 'bottom-0 translate-y-0' : 'bottom-0 translate-y-full'
        }`}
      >
        <div className="flex items-center justify-around py-2">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center gap-1 px-4 py-2 rounded-lg transition-colors ${
                isActive(item.path) 
                  ? 'text-blue-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <span className="text-xl">{item.icon}</span>
              <span className="text-xs font-medium">{item.label}</span>
            </Link>
          ))}
        </div>
      </div>

      {/* Spacer for content when nav is visible */}
      <div className={`h-16 md:h-16 transition-all duration-300 ${isVisible ? 'block' : 'hidden'}`}></div>
    </>
  );
};

export default BottomNav;
